import { Component, ElementRef, Injector, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatDatepicker } from '@angular/material/datepicker';
import { BitfOption } from '@common/libs/bitforce/core/models';
import { debounceTime, skip } from 'rxjs';
import { BitfFormItemComponent } from '../../bitf-form-item.component';

@Component({
  selector: 'bitf-mat-form-item',
  templateUrl: './bitf-mat-form-item.component.html',
  styleUrls: ['./bitf-mat-form-item.component.scss'],
  standalone: false,
})
export class BitfMatFormItemComponent extends BitfFormItemComponent implements OnInit {
  @Input() matFormFieldClass: string;

  @Input() hourOfTheDayMs: number;

  @ViewChild('pickerDate', { static: false })
  pickerDate: MatDatepicker<Date>;

  @ViewChild('chipInput') chipInput: ElementRef<HTMLInputElement>;
  chips: string[] = [];
  chipsObj: any[] = [];

  // Date range form group
  dateRangeGroup: FormGroup;

  constructor(public injector: Injector) {
    super(injector);
  }

  ngOnInit() {
    super.ngOnInit();

    // Initialize date range form group for date-range type
    if (this.formItem.type === 'date-range') {
      this.initDateRangeGroup();
    }

    if (this.formItem.type === 'autocomplete-chips' || this.formItem.type === 'autocomplete') {
      this.initAutocomplete();

      this.formControl.valueChanges.subscribe((value) => {
        // Subscribe to form control value changes in order to re-initialize autocomplete data each times the
        // form control value changes (e.g. when the form has been updated with the server response data).
        // The form control value must be mapped to BitfOption to be able to match it with autocomplete data.
        if (
          Array.isArray(value) &&
          value.some((v) => !(v instanceof BitfOption)) &&
          !(value instanceof BitfOption) &&
          !(typeof value === 'string')
        ) {
          this.initAutocomplete();
        }
      });
    }

    // Watch for external changes to the main form control when using date-range
    if (this.formItem.type === 'date-range') {
      this.formControl.valueChanges.subscribe((value) => {
        if (value && this.dateRangeGroup) {
          this.updateDateRangeFromFormControl(value);
        }
      });
    }
  }

  onAutocompleteBlur() {
    // NOTE: this is needed to reset the form control value when the user types a value
    // that is not in the autocomplete list:BitfOption[]

    if (this.componentConfig.useObject) {
      if (typeof this.formControl.value === 'string') {
        this.formControl.patchValue(null);
      }
    } else {
      if (!this.formItem.options.find((option) => option.id === this.formControl.value)) {
        this.formControl.patchValue(null);
      }
    }
  }

  private initAutocomplete() {
    // map current form values to BitfOption(s)
    if (Array.isArray(this.formControl.value)) {
      if (this.componentConfig.useObject) {
        this.formControl.setValue(
          this.formControl.value.map((value) => this.getBitfOption(value)),
          { emitEvent: false }
        );
      }
    } else if (this.formControl.value) {
      if (this.componentConfig.useObject) {
        this.formControl.setValue(this.getBitfOption(this.formControl.value), { emitEvent: false });
      }
      this.addAutocompleteData(this.formControl.value);
    }

    if (this.formItem.type === 'autocomplete-chips') {
      this.chipsObj = [];
      this.chips = [];

      // create chips
      if (this.formControl.value) {
        const autocompleteChipsFormValue = Array.isArray(this.formControl.value)
          ? this.formControl.value
          : [this.formControl.value];

        this.chipsObj = autocompleteChipsFormValue || [];
        if (this.componentConfig.useObject) {
          this.chips = autocompleteChipsFormValue
            .filter((val) => val && val['label'])
            .map((val) => val['label']);
        } else {
          this.chips = autocompleteChipsFormValue.map((id) => {
            const option = this.componentConfig.autocompleteData?.find((o) => o.id === id);
            return option?.label || String(id);
          });
        }
      }
    }
  }

  isChipSelected(chipValue: number | string): boolean {
    const values = this.formControl?.value;
    if (values?.length) {
      return values.includes(chipValue);
    }
    return false;
  }

  private getBitfOption(value): BitfOption {
    return new BitfOption({
      // The form control value is a BitfOption or an array of BitfOption, we spread values
      // for retrocompatibility with old bitfcore installations.
      ...(this.componentConfig.useObject && value),
      id: this.componentConfig.useObject ? value.id : value,
      label: this.componentConfig.useObject
        ? value[this.componentConfig.autocompleteModelMapKey] || value['label']
        : value,
    });
  }

  private addAutocompleteData(value) {
    const item = this.componentConfig.autocompleteData.find(
      (_item) => _item.id === (this.componentConfig.useObject ? value.id : value)
    );

    if (!item && this.componentConfig.useObject) {
      this.componentConfig.autocompleteData.push(this.getBitfOption(value));
    }
  }

  get filteredAutocompleteData() {
    return this.componentConfig.autocompleteData.filter((x) =>
      this.componentConfig.useObject
        ? !this.chipsObj.map((c) => JSON.stringify(c)).includes(JSON.stringify(x))
        : !this.chips.includes(x['label'])
    );
  }

  displayFn(option: any): string {
    if (option) {
      const item = this.componentConfig.autocompleteData.find(
        (_item) => _item.id === (this.componentConfig.useObject ? option.id : option)
      );
      return item && item['label'];
    }
    return '';
  }

  reloadDate(startDate: Date) {
    const currentDate = this.formControl.value;
    if (startDate > new Date(currentDate)) {
      this.formControl.patchValue(startDate);
    }
  }

  toggleDatePicker() {
    if (this.canAction() && !this.formItem.isDisabled) {
      this.pickerDate.open();
    }
  }

  onDateChange() {
    const hourOfTheDayMs = this.formItem.metaData?.hourOfTheDayMs || this.hourOfTheDayMs;
    if (hourOfTheDayMs) {
      const date: Date = this.formControl.value;
      date.setHours(0, 0, 0, 0);
      this.formControl.patchValue(new Date(date.getTime() + hourOfTheDayMs), {
        emitEvent: false,
      });
    }
  }

  deleteValue() {
    this.formControl.markAsDirty();
    this.formControl.patchValue('');
  }

  removeChip(index) {
    this.chips.splice(index, 1);
    this.chipsObj.splice(index, 1);
    this.formControl.markAsDirty();
    this.formControl.patchValue(this.chipsObj);
    this.selectionChange.emit(this.chipsObj);
  }

  onChipsSelectionChange(event: MatAutocompleteSelectedEvent): void {
    this.chips.push(event.option.viewValue);
    this.chipsObj.push(event.option.value);
    this.formControl.patchValue(this.chipsObj);
    this.chipInput.nativeElement.value = '';
    this.selectionChange.emit(this.chipsObj);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onMatChipInputTokenEnd(_event: MatChipInputEvent): void {
    // NOTE: if the user type a valid option that is converted
    // to a chip, so in case there is a string as value, on the
    // blur event that string will be deleted and replaced with the chipsObj
    // if there are any
    this.formControl.patchValue(this.chipsObj);
    this.chipInput.nativeElement.value = '';
  }

  private initDateRangeGroup(): void {
    // Extract start and end dates from the main form control value
    const currentValue = this.formControl.value;
    let startDate = null;
    let endDate = null;

    if (currentValue) {
      if (Array.isArray(currentValue) && currentValue.length === 2) {
        startDate = currentValue[0];
        endDate = currentValue[1];
      } else if (currentValue.start && currentValue.end) {
        startDate = currentValue.start;
        endDate = currentValue.end;
      }
    }

    this.dateRangeGroup = new FormGroup({
      start: new FormControl(startDate),
      end: new FormControl(endDate),
    });

    // Subscribe to changes in the date range group to update the main form control
    // skip the first emission because it fired from mat-date-range-input initialization
    this.dateRangeGroup.valueChanges.pipe(skip(1), debounceTime(100)).subscribe((value) => {
      if (value.start || value.end) {
        // Update the main form control with the date range as array [start, end]
        this.formControl.patchValue([value.start, value.end], { emitEvent: false });
      } else {
        this.formControl.patchValue(null, { emitEvent: false });
      }
      this.valueChanges.emit();
    });
  }

  onDateRangeChange(): void {
    const hourOfTheDayMs = this.formItem.metaData?.hourOfTheDayMs || this.hourOfTheDayMs;
    if (hourOfTheDayMs) {
      const dateRange = this.dateRangeGroup.value;

      if (dateRange.start) {
        const startDate: Date = new Date(dateRange.start);
        startDate.setHours(0, 0, 0, 0);
        this.dateRangeGroup
          .get('start')
          ?.patchValue(new Date(startDate.getTime() + hourOfTheDayMs), { emitEvent: false });
      }

      if (dateRange.end) {
        const endDate: Date = new Date(dateRange.end);
        endDate.setHours(0, 0, 0, 0);
        this.dateRangeGroup
          .get('end')
          ?.patchValue(new Date(endDate.getTime() + hourOfTheDayMs), { emitEvent: false });
      }
    }
  }

  private updateDateRangeFromFormControl(value: any): void {
    if (!this.dateRangeGroup) return;

    let startDate = null;
    let endDate = null;

    if (Array.isArray(value) && value.length === 2) {
      startDate = value[0];
      endDate = value[1];
    } else if (value && typeof value === 'object' && (value.start || value.end)) {
      startDate = value.start;
      endDate = value.end;
    }

    // Update the date range group without triggering change events to avoid loops
    this.dateRangeGroup.patchValue(
      {
        start: startDate,
        end: endDate,
      },
      { emitEvent: false }
    );
  }
}
